export interface UniformMeta {
  type: 'float' | 'color' | 'vec2' | 'vec3';
  default: number | string | number[];
  min?: number;
  max?: number;
  step?: number;
  label: string;
}

export interface ShaderPresetDef {
  label: string;
  description: string;
  uniforms: Record<string, UniformMeta>;
  vertexShader: string;
  fragmentShader: string;
}

const commonVertex = /* glsl */ `
varying vec3 vNormal;
varying vec3 vViewDir;
varying vec2 vUv;
varying vec3 vWorldPos;

void main() {
  vUv = uv;
  vNormal = normalize(normalMatrix * normal);
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = worldPos.xyz;
  vViewDir = normalize(cameraPosition - worldPos.xyz);
  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

export const SHADER_PRESETS: Record<string, ShaderPresetDef> = {
  holographic: {
    label: 'Holographic',
    description: 'Iridescent color shifting based on view angle',
    uniforms: {
      speed: { type: 'float', default: 1.0, min: 0.1, max: 5.0, step: 0.1, label: 'Speed' },
      intensity: { type: 'float', default: 1.0, min: 0.0, max: 2.0, step: 0.05, label: 'Intensity' },
      tint: { type: 'color', default: '#ffffff', label: 'Base Tint' },
    },
    vertexShader: commonVertex,
    fragmentShader: /* glsl */ `
uniform float time;
uniform float speed;
uniform float intensity;
uniform vec3 tint;

varying vec3 vNormal;
varying vec3 vViewDir;
varying vec2 vUv;

void main() {
  float fresnel = 1.0 - dot(vNormal, vViewDir);
  fresnel = pow(fresnel, 2.0);

  float shift = time * speed + dot(vNormal, vec3(1.0, 0.5, 0.25)) * 3.0;

  vec3 col;
  col.r = sin(shift) * 0.5 + 0.5;
  col.g = sin(shift + 2.094) * 0.5 + 0.5;
  col.b = sin(shift + 4.189) * 0.5 + 0.5;

  col = mix(tint * 0.3, col, fresnel * intensity);
  col += fresnel * 0.3 * intensity;

  gl_FragColor = vec4(col, 0.9 + fresnel * 0.1);
}
    `,
  },

  metallicFlake: {
    label: 'Metallic Flake',
    description: 'Sparkling metallic particles',
    uniforms: {
      flakeScale: { type: 'float', default: 50.0, min: 10.0, max: 200.0, step: 1.0, label: 'Flake Scale' },
      sparkle: { type: 'float', default: 0.8, min: 0.0, max: 1.0, step: 0.05, label: 'Sparkle' },
      baseColor: { type: 'color', default: '#222222', label: 'Base Color' },
      flakeColor: { type: 'color', default: '#ffffff', label: 'Flake Color' },
    },
    vertexShader: commonVertex,
    fragmentShader: /* glsl */ `
uniform float time;
uniform float flakeScale;
uniform float sparkle;
uniform vec3 baseColor;
uniform vec3 flakeColor;

varying vec3 vNormal;
varying vec3 vViewDir;
varying vec3 vWorldPos;

// Simple hash for flake positions
float hash(vec3 p) {
  p = fract(p * vec3(443.897, 441.423, 437.195));
  p += dot(p, p.yzx + 19.19);
  return fract((p.x + p.y) * p.z);
}

void main() {
  vec3 scaledPos = vWorldPos * flakeScale;
  vec3 cellId = floor(scaledPos);

  float flake = hash(cellId);
  float viewDot = dot(vNormal, vViewDir);

  // Flakes sparkle based on view angle
  float flakeIntensity = smoothstep(0.7, 1.0, flake) * sparkle;
  float sparkleAnim = sin(time * 3.0 + flake * 6.28) * 0.5 + 0.5;
  flakeIntensity *= sparkleAnim;

  // Fresnel for metallic look
  float fresnel = pow(1.0 - max(viewDot, 0.0), 3.0);

  vec3 col = baseColor;
  col += flakeColor * flakeIntensity;
  col += fresnel * 0.2;
  col *= 0.5 + viewDot * 0.5; // Basic lighting

  gl_FragColor = vec4(col, 1.0);
}
    `,
  },

  neonGlow: {
    label: 'Neon Glow',
    description: 'Fresnel-based emissive edge glow',
    uniforms: {
      glowColor: { type: 'color', default: '#00f5ff', label: 'Glow Color' },
      glowIntensity: { type: 'float', default: 2.0, min: 0.0, max: 10.0, step: 0.1, label: 'Intensity' },
      fresnelPower: { type: 'float', default: 2.0, min: 0.5, max: 5.0, step: 0.1, label: 'Edge Sharpness' },
      baseColor: { type: 'color', default: '#111111', label: 'Base Color' },
      pulseSpeed: { type: 'float', default: 1.0, min: 0.0, max: 5.0, step: 0.1, label: 'Pulse Speed' },
    },
    vertexShader: commonVertex,
    fragmentShader: /* glsl */ `
uniform float time;
uniform vec3 glowColor;
uniform float glowIntensity;
uniform float fresnelPower;
uniform vec3 baseColor;
uniform float pulseSpeed;

varying vec3 vNormal;
varying vec3 vViewDir;

void main() {
  float fresnel = 1.0 - dot(vNormal, vViewDir);
  fresnel = pow(fresnel, fresnelPower);

  float pulse = pulseSpeed > 0.0 ? sin(time * pulseSpeed) * 0.3 + 0.7 : 1.0;

  vec3 col = baseColor;
  col += glowColor * fresnel * glowIntensity * pulse;

  gl_FragColor = vec4(col, 0.85 + fresnel * 0.15);
}
    `,
  },

  glass: {
    label: 'Glass',
    description: 'Transparent with Fresnel reflection',
    uniforms: {
      tint: { type: 'color', default: '#aaddff', label: 'Tint' },
      baseOpacity: { type: 'float', default: 0.15, min: 0.0, max: 1.0, step: 0.01, label: 'Base Opacity' },
      fresnelPower: { type: 'float', default: 3.0, min: 0.5, max: 8.0, step: 0.1, label: 'Fresnel Power' },
      reflectivity: { type: 'float', default: 0.5, min: 0.0, max: 1.0, step: 0.05, label: 'Reflectivity' },
    },
    vertexShader: commonVertex,
    fragmentShader: /* glsl */ `
uniform vec3 tint;
uniform float baseOpacity;
uniform float fresnelPower;
uniform float reflectivity;

varying vec3 vNormal;
varying vec3 vViewDir;

void main() {
  float fresnel = 1.0 - dot(vNormal, vViewDir);
  fresnel = pow(fresnel, fresnelPower);

  vec3 col = tint * (0.3 + fresnel * 0.7);
  col += vec3(1.0) * fresnel * reflectivity;

  float alpha = baseOpacity + fresnel * (1.0 - baseOpacity);

  gl_FragColor = vec4(col, alpha);
}
    `,
  },

  carbonFiber: {
    label: 'Carbon Fiber',
    description: 'Procedural woven carbon fiber pattern',
    uniforms: {
      scale: { type: 'float', default: 30.0, min: 5.0, max: 100.0, step: 1.0, label: 'Pattern Scale' },
      baseColor: { type: 'color', default: '#1a1a1a', label: 'Base Color' },
      highlightColor: { type: 'color', default: '#444444', label: 'Highlight' },
      glossiness: { type: 'float', default: 0.8, min: 0.0, max: 1.0, step: 0.05, label: 'Glossiness' },
    },
    vertexShader: commonVertex,
    fragmentShader: /* glsl */ `
uniform float scale;
uniform vec3 baseColor;
uniform vec3 highlightColor;
uniform float glossiness;

varying vec3 vNormal;
varying vec3 vViewDir;
varying vec2 vUv;

void main() {
  vec2 uv = vUv * scale;

  // Weave pattern
  float weaveX = sin(uv.x * 3.14159) * 0.5 + 0.5;
  float weaveY = sin(uv.y * 3.14159) * 0.5 + 0.5;
  float pattern = step(0.5, mod(floor(uv.x) + floor(uv.y), 2.0));
  float weave = mix(weaveX, weaveY, pattern);

  vec3 col = mix(baseColor, highlightColor, weave * 0.5);

  // Fresnel for glossy look
  float fresnel = pow(1.0 - dot(vNormal, vViewDir), 3.0);
  col += vec3(1.0) * fresnel * glossiness * 0.3;

  // Basic directional lighting
  float light = dot(vNormal, normalize(vec3(1.0, 1.0, 0.5))) * 0.5 + 0.5;
  col *= light;

  gl_FragColor = vec4(col, 1.0);
}
    `,
  },
};
